import { useScrollReveal } from "@/hooks/useScrollReveal";
import SiteFooter from "./SiteFooter";

interface WorkProject {
  label: string;
  title: string;
  description: string;
  cta: string;
  link: string;
  external?: boolean;
  hasDitheredBg?: boolean;
}

const WorkCard = ({ project, index }: { project: WorkProject; index: number }) => {
  const { ref, isVisible } = useScrollReveal(0.15);

  return (
    <article
      ref={ref as React.RefObject<HTMLElement>}
      className={`glass brutalist scanline p-8 md:p-12 relative overflow-hidden transition-all duration-1000 ease-out ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
      }`}
      style={{ transitionDelay: `${index * 150}ms` }}
    >
      {project.hasDitheredBg && (
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage: `radial-gradient(circle at 20% 50%, hsl(var(--foreground)) 1px, transparent 1px),
              radial-gradient(circle at 80% 30%, hsl(var(--foreground)) 1px, transparent 1px),
              radial-gradient(circle at 40% 70%, hsl(var(--foreground)) 0.5px, transparent 0.5px)`,
            backgroundSize: "60px 60px, 80px 80px, 40px 40px",
          }}
        />
      )}

      <div className="relative z-10">
        <span className="text-micro text-dim uppercase tracking-[0.2em] block mb-6">{project.label}</span>

        <h3 className="text-title font-serif mb-6">{project.title}</h3>

        <p className="text-body text-subtle leading-loose mb-8 max-w-2xl">{project.description}</p>

        <a
          href={project.link}
          target={project.external ? "_blank" : undefined}
          rel={project.external ? "noopener noreferrer" : undefined}
          className="inline-block bg-foreground text-background px-8 py-3 text-caption uppercase tracking-[0.15em] font-semibold hover:bg-subtle transition-colors duration-300"
        >
          {project.cta}
        </a>
      </div>
    </article>
  );
};

const WorkSection = () => {
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal(0.3);

  const projects: WorkProject[] = [
    {
      label: "Intelligence Feed 01",
      title: "Humaniti (2025)",
      description:
        "Real-time ethical crisis intelligence for displaced and undocumented communities. Encrypted diaspora signals, open satellite feeds, humanitarian overlays.",
      cta: "Access Humaniti",
      link: "https://humaniti.online/",
      external: true,
      hasDitheredBg: true,
    },
    {
      label: "Research Module 02",
      title: "Gorni (2026)",
      description:
        "Hospital bed interface reducing missed complications and improving nurse-patient communication. Pre-TRL3 stage — ideation and prototype.",
      cta: "Learn More",
      link: "/contact",
    },
    {
      label: "Experiment 03",
      title: "KILLk.AI (2025)",
      description:
        "Interactive narrative exploring AI consent and emotional dependency. Story-driven mechanics examining the boundaries of artificial intimacy.",
      cta: "Explore",
      link: "https://killk.ai",
      external: true,
    },
    {
      label: "Cultural Archive 04",
      title: "Frantik (2024)",
      description:
        "Speculative design and fashion exploring identity, conflict, and resistance. Cultural exploration through artifacts.",
      cta: "Explore",
      link: "/contact",
    },
    {
      label: "Memory Infrastructure 05",
      title: "Lineage.ai (2024) [V0.1]",
      description:
        "Encrypted ancestry and oral history preservation for the Global South. Offline-capable, voice-first memory architecture with consent-driven access.",
      cta: "Learn More",
      link: "/lineage",
    },
  ];

  return (
    <div className="w-full h-full overflow-y-auto">
      <section id="work" className="py-section px-6 md:px-10">
        <div className="max-w-5xl mx-auto">
          <div
            ref={headerRef as React.RefObject<HTMLDivElement>}
            className={`border-t-2 border-rule pt-8 mb-block transition-all duration-1000 ease-out ${
              headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            <h2 className="text-caption text-dim uppercase tracking-[0.2em]">Work</h2>
          </div>

          <div className="grid grid-cols-1 gap-0">
            {projects.map((project, index) => (
              <WorkCard key={index} project={project} index={index} />
            ))}
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default WorkSection;
